import Image from 'next/legacy/image';

import { useState } from 'react';
import { MembersOnlyLock } from './MembersOnlyLock';
import Icon from '@components/atoms/icon';
import {
  BodyRegularBold,
  BodySmallRegular,
  H4
} from '@components/atoms/typography';
import {
  CHIP_CONTENT,
  CHIP_IMAGES,
  CHIP_ORDER,
  type ChipSelectionState,
  type ChipSelections
} from '@lib/my-team/utils/chip-strategy';
import type { ChipType } from '@api-service/planner';

const buildSubText = (selection: ChipSelectionState): string => {
  const parts: string[] = [];
  if (selection.playedGameweeks.length > 0) {
    parts.push(`Played in GW${selection.playedGameweeks.join(', GW')}`);
  }
  if (selection.selectedGameweek !== null) {
    parts.push(`Active in GW${selection.selectedGameweek}`);
  }
  if (parts.length === 0) return 'Not set';
  return parts.join(' · ');
};

type ChipBannerProps = {
  chipSelections: ChipSelections;
  onSelectChip: (chipType: ChipType) => void;
  /** Non-paying members see chip strategy padlocked and non-interactive. */
  isLocked?: boolean;
};

export const ChipBanner = ({
  chipSelections,
  onSelectChip,
  isLocked = false
}: ChipBannerProps) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div
      className="rounded-lg bg-neutral-100 px-3 md:px-5 py-6 w-fit"
      data-cy="chip-strategy-banner"
    >
      <div
        onClick={() => setIsOpen((current) => !current)}
        className="flex justify-between items-center cursor-pointer gap-2"
      >
        <span className="flex items-center gap-1">
          <H4
            data-cy="chip-strategy-accordion-title"
            className="text-center md:text-start"
          >
            Your chip strategy
          </H4>
          {isLocked && (
            <MembersOnlyLock
              feature="chip strategy"
              placement="pick_chip_banner"
              dataCy="chip-strategy-lock"
            />
          )}
        </span>
        <Icon
          className={`transform transition-transform duration-300 ${
            isOpen ? 'rotate-90' : 'rotate-0'
          }`}
          name="ChevronRight"
          fontSize="28px"
        />
      </div>
      <div
        className={`transition-max-height duration-300 ease-in-out overflow-hidden ${
          isOpen ? 'max-h-screen' : 'max-h-0'
        }`}
      >
        <div className="mt-6 flex flex-wrap lg:flex-nowrap gap-y-5 justify-center md:justify-start">
          {CHIP_ORDER.map((chipType) => {
            const selection = chipSelections[chipType];
            const clickIsBlocked = isLocked || !selection.canActivate;

            return (
              <div
                key={chipType}
                data-cy={`chip-${chipType.toLowerCase()}`}
                onClick={() => {
                  if (clickIsBlocked) return;
                  onSelectChip(chipType);
                }}
                className={`relative ${
                  clickIsBlocked
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:bg-neutral-200 transition-colors duration-300 ease-in-out'
                } bg-white rounded-lg px-3 py-3 md:py-5 flex flex-row md:flex-col items-center w-full md:w-[140px] justify-between mr-0 md:mr-3 last:mr-0`}
              >
                <div className="flex flex-row items-center md:flex-col">
                  <div className="w-[48px] h-[48px] rounded-full bg-primary-100 flex justify-center items-center md:mb-3">
                    <Image
                      className="animate-sparkle"
                      src={CHIP_IMAGES[chipType]}
                      alt={CHIP_CONTENT[chipType].label}
                      height={24}
                      width={24}
                    />
                  </div>
                  <BodyRegularBold className="md:mb-1 ml-4 md:ml-0 text-center">
                    {CHIP_CONTENT[chipType].label}
                  </BodyRegularBold>
                </div>
                <BodySmallRegular
                  className="text-center"
                  textColor="text-neutral-600"
                >
                  {buildSubText(selection)}
                </BodySmallRegular>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
